/*
 * Top-level view routing.
 *
 * The app has three entry surfaces and no router library: a landing chooser, the
 * manual Console, and Ada (the assistant). They render in place of one another
 * from index.js rather than nesting, so switching is a pushState + re-render —
 * no page load, no popup window.
 *
 * State lives in the ?view= query param rather than the path so one build works
 * under any PUBLIC_URL: the multi-node dashboard serves from /, the on-device
 * console from /console/. A path-based router would need an nginx rule and a
 * basename kept in sync with both.
 */

// ASSISTANT_NAME is the assistant's product name.
//
// This is the only copy on the front end. The edge's system prompt names it too
// (cloud-anchor/pkg/nabxagent/prompt.go), because a byte-stable prompt is what
// lets the whole request prefix cache; rename in both.
export const ASSISTANT_NAME = "Ada";
export const CONSOLE_NAME = "Console";

export const VIEW_LANDING = "landing";
export const VIEW_CONSOLE = "console";
export const VIEW_ASSISTANT = "assistant";

/* Which way a surface change should slide. Set on navigation and read once by
   the incoming surface, because "did I arrive from Ada or from the chooser" is
   the difference between a transition that explains the move and one that just
   flashes. */
let enterFrom = "";

export const takeEnterDirection = () => {
  const d = enterFrom;
  enterFrom = "";
  return d;
};

const PARAM = "view";
const NODE_PARAM = "node";
const PANEL_PARAM = "panel";
const APPROVAL_PARAM = "approval";

const listeners = new Set();

/** readView returns the view named by the current URL. */
export const readView = () => {
  if (typeof window === "undefined") return VIEW_LANDING;
  const v = new URLSearchParams(window.location.search).get(PARAM);
  if (v === VIEW_ASSISTANT) return VIEW_ASSISTANT;
  if (v === VIEW_CONSOLE) return VIEW_CONSOLE;
  // An approver's emailed link opens the console on the approval it names.
  if (readApproval()) return VIEW_CONSOLE;
  return VIEW_LANDING;
};

/** readNode returns the ?node= deep-link target, if any. */
export const readNode = () => {
  if (typeof window === "undefined") return "";
  return new URLSearchParams(window.location.search).get(NODE_PARAM) || "";
};

/**
 * readPanel returns the console panel to open on arrival, if any.
 *
 * The Console's own panel selection is component state, not URL state — it is
 * navigated by clicking the sidebar, and nothing needed to address it from
 * outside. Ada does: she proposes a change and then has to put the operator in
 * front of the panel where they can see it took effect. This param is that
 * entry point, and is read once on mount.
 */
export const readPanel = () => {
  if (typeof window === "undefined") return "";
  return new URLSearchParams(window.location.search).get(PANEL_PARAM) || "";
};

/**
 * readApproval returns the approval to open on arrival, if any. The email an
 * approver is sent (server/pkg/approvals) links to <console>/#approval=<id>,
 * the change waiting for their yes, in the fragment so it reaches no access
 * log. Read once on mount, as ?panel= is.
 */
export function readApproval() {
  if (typeof window === "undefined") return "";
  return new URLSearchParams(window.location.hash.slice(1)).get(APPROVAL_PARAM) || "";
}

/**
 * syncNode keeps ?node= truthful after the operator switches edges by hand.
 *
 * Without it the param is only ever written on the way in, so a reload — or a
 * shared link — reopens the console on whichever edge was deep-linked hours
 * ago. Replaces rather than pushes: changing edge is not a navigation the back
 * button should walk through.
 */
export const syncNode = (nodeId) => {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (url.searchParams.get(NODE_PARAM) === (nodeId || "")) return;
  if (nodeId) url.searchParams.set(NODE_PARAM, nodeId);
  else url.searchParams.delete(NODE_PARAM);
  window.history.replaceState({}, "", url.toString());
};

/**
 * navigateTo switches surfaces without reloading. History is pushed so the
 * browser's back button returns to the landing page, which is what someone
 * expects after picking one of two doors.
 */
export const navigateTo = (view, { node, panel, replace = false, from } = {}) => {
  if (typeof window === "undefined") return;
  enterFrom = from || "";
  const url = new URL(window.location.href);
  url.searchParams.delete(PANEL_PARAM);
  if (view === VIEW_LANDING) {
    url.searchParams.delete(PARAM);
    url.searchParams.delete(NODE_PARAM);
  } else {
    url.searchParams.set(PARAM, view);
    if (node) url.searchParams.set(NODE_PARAM, node);
    if (panel) url.searchParams.set(PANEL_PARAM, panel);
  }
  const next = url.toString();
  if (replace) window.history.replaceState({}, "", next);
  else window.history.pushState({}, "", next);
  listeners.forEach((fn) => fn());
};

/**
 * consoleHref is the address of the Console served at another origin, such as
 * another region's: its root alone opens the landing page, not the Console.
 */
export const consoleHref = (origin) => {
  const url = new URL("/", origin);
  url.searchParams.set(PARAM, VIEW_CONSOLE);
  return url.toString();
};

/** subscribe fires on both programmatic navigation and browser back/forward. */
export const subscribe = (fn) => {
  listeners.add(fn);
  const onPop = () => fn();
  window.addEventListener("popstate", onPop);
  return () => {
    listeners.delete(fn);
    window.removeEventListener("popstate", onPop);
  };
};
